import { FunctionComponent, useState } from "react";

import { faRotateLeft } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { useSnackBar } from "@/utils/useSnackbar";

import { useRestoreDynamicsDefaultFieldMappingsMutation } from "@/pages/Private/redux/dynamics/dynamics.api";

import { MsdynamicsEntityType } from "../schema/dynamics";

interface DynamicsRestoreDefaultsButtonProps {
	/** The tab's entity — only its defaults are restored, not all four. */
	entityType: MsdynamicsEntityType;
	ts: (key: string, options?: Record<string, unknown>) => string;
}

/**
 * Re-adds the out-of-the-box mappings of one tab — the same set a fresh Dynamics 365
 * connection is seeded with. The endpoint only fills gaps, so this never overwrites or
 * duplicates a mapping the customer configured themselves; when nothing was missing it
 * returns an empty list, which is reported rather than left as a silent no-op.
 */
export const DynamicsRestoreDefaultsButton: FunctionComponent<
	DynamicsRestoreDefaultsButtonProps
> = ({ entityType, ts }) => {
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();
	const [restoreDefaults] = useRestoreDynamicsDefaultFieldMappingsMutation();
	const [isRestoring, setIsRestoring] = useState(false);

	const handleRestore = async () => {
		setIsRestoring(true);

		try {
			const created = await restoreDefaults({ entityType }).unwrap();

			if (!created.length) {
				handleOpenSnackbar(ts("restoreDefaultsNoneAdded"));
			}
		} catch {
			handleOpenSnackbar(ts("restoreDefaultsFailed"));
		} finally {
			setIsRestoring(false);
		}
	};

	return (
		<>
			{renderMessageSnackbar()}
			<span title={ts("restoreDefaultsTooltip")}>
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					image={<Icon className="w-[16px] h-[16px] text-text mr-2" icon={faRotateLeft} />}
					isLoading={isRestoring}
					size={ButtonSize.S}
					title={ts("restoreDefaults")}
					onClick={handleRestore}
				/>
			</span>
		</>
	);
};
